import { useState } from "react";
import { useSearch } from "@tanstack/react-router";
import { Flex, Text } from "styles/utility-components";

import Alert from "components/ui/alert/Alert";
import Button from "components/ui/button/Button";
import { betterAuthErrorText } from "lib/auth/authClient";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { localRedirectKind } from "lib/hooks/api/auth/mcp/schemas";
import { splitHost, useOAuthClientDetails } from "lib/hooks/api/auth/useOAuthClientDetails";
import { useOAuthConsent } from "lib/hooks/api/auth/useOAuthConsent";
import { Card, Page } from "./authPageStyles";
import { StotlesLogo } from "./StotlesLogo";

/** Every scope a self-registering client can ask for, in words a user can act on. */
const SCOPE_DESCRIPTIONS: Record<string, string> = {
  openid: "Confirm who you are",
  profile: "Read your name",
  email: "Read your email address",
  "mcp:read": "Read your Stotles data — notices, buyers, suppliers and documents",
  "mcp:write": "Make changes in your Stotles account on your behalf",
  offline_access: "Stay connected without asking you again each time",
};

function describeScope(scope: string): string {
  // A scope we forgot to describe still gets shown, rather than hidden from the person approving it.
  return SCOPE_DESCRIPTIONS[scope] ?? scope;
}

/** The individual claims a client asked for by name, on top of what its scopes already imply. */
function requestedClaims(claims: string | undefined): string[] {
  if (!claims) return [];
  try {
    const parsed = JSON.parse(claims) as { userinfo?: Record<string, unknown> };
    return Object.keys(parsed.userinfo ?? {});
  } catch {
    // The auth-service validates claims properly when the decision is posted; showing nothing here
    // beats showing garbage.
    return [];
  }
}

/**
 * The host:port a local callback sends the code to, or `undefined` for a hosted one. Only the local
 * forms registration accepts can get here, since the provider has already matched the URI. Guarded,
 * because the param is display-only and an edited URL can carry a port `URL` refuses.
 */
function localCallbackHost(redirectUri: string | undefined): string | undefined {
  if (!redirectUri || !localRedirectKind(redirectUri)) return undefined;
  try {
    return new URL(redirectUri).host;
  } catch {
    return undefined;
  }
}

/**
 * Consent screen for a third-party OAuth client. Stotles' own apps are registered with `skipConsent`,
 * so only MCP clients reach it.
 *
 * Any signed-in user can connect, whatever their plan: app-service refuses an unpaid caller's tool
 * calls instead, so an IT admin on a free seat can still set a client up.
 *
 * The signed query has to go back as `oauth_query` for the provider to resume the request it paused.
 * It's read off `window.location.search` rather than `useSearch` because re-serialising through the
 * router renames the repeated `ba_param` entries to `ba_param[]` and breaks the signature.
 */
export function BetterAuthConsentPage() {
  // Read once — a later navigation would drop these params.
  const [oauthQuery] = useState(() => window.location.search.replace(/^\?/, ""));
  // Display only. Sanitised by the route's validateConsentSearch.
  const {
    client_id: clientId,
    scope,
    claims,
    redirect_uri: redirectUri,
  } = useSearch({ strict: false });

  const { data: client, isLoading: isLoadingClient } = useOAuthClientDetails(clientId, oauthQuery);

  const {
    mutate: recordConsent,
    isLoading: isSubmitting,
    isError,
    error,
  } = useOAuthConsent({
    // A full navigation: the destination is outside this SPA.
    onSuccess: (url) => window.location.assign(url),
  });

  const scopes = scope?.split(" ").filter(Boolean) ?? [];
  const namedClaims = requestedClaims(claims);
  // No signature means we didn't arrive from the auth-service, so there is no request to resume.
  const hasSignedQuery = new URLSearchParams(oauthQuery).has("sig");

  const submit = (accept: boolean) => recordConsent({ accept, oauthQuery });

  if (!hasSignedQuery) {
    return (
      <Page>
        <Card centred>
          <StotlesLogo />
          <Text h1>Nothing to authorise</Text>
          <Text color="sysTextSecondary">
            This page is only reachable while signing in to a connected application.
          </Text>
        </Card>
      </Page>
    );
  }

  // The auth-service always sends both, so one missing means an edited URL. With nothing truthful to
  // show, don't ask for a decision — but keep Cancel, so the waiting client gets `access_denied`
  // rather than hanging. (`claims` is optional; most clients don't send one.)
  if (!clientId || scopes.length === 0) {
    return (
      <Page>
        <Card centred>
          <StotlesLogo />
          <Text h1>Something's missing from this request</Text>
          <Text color="sysTextSecondary">
            We can't tell you what this application is asking for, so we won't ask you to approve
            it. Try connecting again from the application.
          </Text>
          <Button
            buttonType="secondary"
            fullWidth
            disabled={isSubmitting}
            onClick={() => submit(false)}
          >
            Cancel
          </Button>

          {isError && (
            <Text color="sysDestructiveDefault">
              {betterAuthErrorText(error, "We couldn't save your consent decision.")}
            </Text>
          )}
        </Card>
      </Page>
    );
  }

  // Wait for the client lookup, or the card visibly rewrites itself from "An application" to a name.
  if (isLoadingClient) {
    return (
      <Page>
        <Card centred>
          <StotlesLogo />
          <CentredSpinner label="Loading" />
        </Card>
      </Page>
    );
  }

  const registeredBy = client?.host ? splitHost(client.host) : undefined;
  const localHost = localCallbackHost(redirectUri);

  return (
    <Page>
      <Card centred>
        <StotlesLogo />
        <Text h1>Authorise access</Text>
        <Flex column gap={24}>
          <Text color="sysTextSecondary">
            <strong>{client?.name ?? client?.identity ?? "An application"}</strong> is asking to use
            your Stotles account.
          </Text>

          {/* A client picks its own name; the host serving its metadata document is what it can't fake.
            Only self-registering clients have one — a provisioned client's id is an opaque uuid.
            The end of the host is emphasised because that's the part that says who owns it:
            `stotles.com.attacker.com` borrows our name at the front and is attacker.com's server. */}
          {registeredBy ? (
            <Text description color="sysTextSecondary">
              Registered by {registeredBy.lead}
              <strong>{registeredBy.owner}</strong>
            </Text>
          ) : (
            <Text description color="sysTextSecondary">
              This is a third-party application, not one managed by Stotles.
            </Text>
          )}

          {/* A local callback means the access goes to whatever is listening on that port, which we can't verify. */}
          {localHost && (
            <Alert type="warning">
              This request came from software running on your own computer, and access will be
              handed to whatever is listening at <strong>{localHost}</strong>. Only allow it if you
              have just started connecting a tool on this device.
            </Alert>
          )}

          <Flex column>
            <Text color="sysTextSecondary">It will be able to:</Text>
            <ul>
              {scopes.map((s) => (
                <li key={s}>
                  <Text color="sysTextSecondary">{describeScope(s)}</Text>
                </li>
              ))}
            </ul>
          </Flex>

          {namedClaims.length > 0 && (
            <Flex column>
              <Text color="sysTextSecondary">It has also asked for:</Text>
              <ul>
                {namedClaims.map((claim) => (
                  <li key={claim}>
                    <Text color="sysTextSecondary">{claim}</Text>
                  </li>
                ))}
              </ul>
            </Flex>
          )}

          <Flex column gap={12}>
            <Button
              buttonType="primary"
              fullWidth
              loading={isSubmitting}
              onClick={() => submit(true)}
            >
              Allow
            </Button>
            <Button
              buttonType="secondary"
              fullWidth
              disabled={isSubmitting}
              onClick={() => submit(false)}
            >
              Deny
            </Button>
          </Flex>

          {isError && (
            <Text color="sysDestructiveDefault">
              {betterAuthErrorText(error, "We couldn't save your consent decision.")}
            </Text>
          )}
        </Flex>
      </Card>
    </Page>
  );
}
